import {
  FLEX_TRACK,
  gridMinWidth,
  trackFor,
  type AvatarOptions,
  type BadgeOptions,
  type ColumnWidths,
} from '@document-explorer/core';
import type { ColumnDef } from './types.js';

/** Kept byte-for-byte in step with the React package's defaults. */
export const DEFAULT_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', sortable: true, width: FLEX_TRACK, minWidth: 160, optional: false },
  { key: 'type', label: 'Type', sortable: true, width: '110px' },
  { key: 'size', label: 'Size', sortable: true, numeric: true, width: '90px' },
  { key: 'updatedAt', label: 'Modified', sortable: true, width: '130px' },
];

const KNOWN = new Map(DEFAULT_COLUMNS.map((c) => [c.key, c]));

export interface AvatarColumnOptions extends AvatarOptions, Omit<ColumnDef, 'key' | 'label'> {
  /** Where the name lives, e.g. `'metadata.owner'`. */
  key: string;
  /** Header text. Derived from the key when omitted. */
  label?: string;
}

/** A column definition plus the avatar options its cell template should use. */
export interface AvatarColumn {
  column: ColumnDef;
  options: AvatarOptions;
}

/**
 * Build a column that renders a person.
 *
 * Angular cannot return markup from a plain object the way React's `render`
 * can, so this hands back the column *and* the options to feed
 * `<de-avatar-cell>` inside a matching `deColumn` template:
 *
 * ```ts
 * readonly owner = avatarColumn({ key: 'metadata.owner', display: 'avatar' });
 * readonly columns = ['name', this.owner.column, 'size'];
 * ```
 * ```html
 * <ng-template deColumn="metadata.owner" let-item>
 *   <de-avatar-cell [item]="item" columnKey="metadata.owner" [options]="owner.options" />
 * </ng-template>
 * ```
 */
export function avatarColumn(options: AvatarColumnOptions): AvatarColumn {
  const { key, label, name, image, display, tooltip, color, fallback, ...rest } = options;

  return {
    column: {
      sortable: true,
      // Avatar-only cells are narrow by nature; name cells need room to breathe.
      width: display === 'avatar' ? '80px' : '170px',
      minWidth: display === 'avatar' ? 56 : 100,
      ...rest,
      key,
      label: label ?? labelFor(key),
    },
    options: { name, image, display, tooltip, color, fallback },
  };
}

export interface BadgeColumnOptions extends BadgeOptions, Omit<ColumnDef, 'key' | 'label'> {
  /** Where the value lives, e.g. `'metadata.status'`. */
  key: string;
  /** Header text. Derived from the key when omitted. */
  label?: string;
}

/** A column definition plus the badge options its cell template should use. */
export interface BadgeColumn {
  column: ColumnDef;
  options: BadgeOptions;
}

/**
 * Build a column that renders a status or state.
 *
 * Common values get a conventional tone with no configuration — `approved` is
 * green, `pending` amber, `rejected` red — and every part of that is
 * overridable via `tones`, `format` and `variant`. Colours come from the
 * `--de-tone-*` tokens.
 *
 * ```ts
 * readonly status = badgeColumn({ key: 'metadata.status', variant: 'pill' });
 * ```
 * ```html
 * <ng-template deColumn="metadata.status" let-item>
 *   <de-badge-cell [item]="item" columnKey="metadata.status" [options]="status.options" />
 * </ng-template>
 * ```
 */
export function badgeColumn(options: BadgeColumnOptions): BadgeColumn {
  const { key, label, value, tones, format, variant, fallbackTone, ...rest } = options;

  return {
    column: {
      sortable: true,
      width: '130px',
      minWidth: 80,
      ...rest,
      key,
      label: label ?? labelFor(key),
    },
    options: { value, tones, format, variant, fallbackTone },
  };
}

function labelFor(key: string): string {
  const last = key.split('.').pop() ?? key;
  return last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

export function resolveColumns(columns: (ColumnDef | string)[] | undefined): ColumnDef[] {
  if (!columns || columns.length === 0) return DEFAULT_COLUMNS;
  return columns.map((column) => {
    if (typeof column !== 'string') return { optional: column.key !== 'name', ...column };
    return KNOWN.get(column) ?? { key: column, label: labelFor(column), sortable: true, optional: true };
  });
}

export function gridTemplate(
  columns: ColumnDef[],
  hasCheckbox: boolean,
  hasActions: boolean,
  widths: ColumnWidths = {},
): string {
  const tracks = columns.map((c) => trackFor(c.key, c.width, widths, c.minWidth));
  if (hasCheckbox) tracks.unshift('16px');
  if (hasActions) tracks.push('32px');
  return tracks.join(' ');
}

/**
 * The narrowest the row may be. Without it the row stays at container width
 * while its tracks overflow, so scrolling right leaves the hover highlight,
 * selection background and bottom border behind.
 */
export function gridMinimum(
  columns: ColumnDef[],
  hasCheckbox: boolean,
  hasActions: boolean,
  widths: ColumnWidths = {},
): string {
  return gridMinWidth(columns, hasCheckbox, hasActions, widths);
}

export function narrowGridTemplate(hasCheckbox: boolean, hasActions: boolean): string {
  const tracks = [FLEX_TRACK];
  if (hasCheckbox) tracks.unshift('16px');
  if (hasActions) tracks.push('32px');
  return tracks.join(' ');
}
